import { useState, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api/api";
import { plural } from "@lingui/core/macro";
import { CircleDollarSign, LayoutList, CookingPot, TriangleAlert } from "lucide-react";
import type { Recipe } from "@/types/recipe";
import type { CostSummary } from "@/lib/api/requests/recipes";
import type { Uom } from "@/types/material-conversion";
import { useCostSummary, useRecipeUsedIn, useUpdateRecipe } from "@/hooks/recipes";
import { useUoms } from "@/hooks/useUomReference";
import { formatMoney, formatNumber } from "@/lib/materials/conversion-calc";
import { uomLabel } from "@/lib/materials/conversion-grid";
import { RecipeUsageListing } from "@/components/recipes/usage/RecipeUsageListing";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { statCardSurface } from "@/components/common/statCardSurface";
import { formatCurrency } from "@/components/recipes/cost/CostValue";

interface CostFinancialsTabProps {
  recipe: Recipe;
  readOnly?: boolean;
}

export function CostFinancialsTab({ recipe, readOnly = false }: CostFinancialsTabProps) {
  const { t } = useLingui();
  const { data: costSummary, isLoading } = useCostSummary(recipe.id);
  const updateRecipe = useUpdateRecipe();

  const [sellingPrice, setSellingPrice] = useState<string>(
    recipe.targetSellingPrice != null ? String(recipe.targetSellingPrice) : ""
  );

  function handleApply() {
    const parsed = sellingPrice === "" ? null : parseFloat(sellingPrice);
    if (sellingPrice === "" || (parsed != null && !isNaN(parsed) && parsed >= 0)) {
      updateRecipe.mutate(
        { id: recipe.id, data: { targetSellingPrice: parsed } },
        {
          onSuccess: () => toast.success(t`Selling price saved`),
          onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to save selling price`)),
        },
      );
    }
  }

  // Food cost and margin come from the endpoint rather than being recomputed
  // here, so the recipe screen, an export and a menu report cannot each round
  // differently (T-606). The consequence is that they follow the *saved* price:
  // a typed-but-unapplied figure is a draft, and `priceIsDraft` says so instead
  // of quietly previewing a number nothing has agreed to yet.
  const savedSellingPrice = costSummary?.targetSellingPrice ?? recipe.targetSellingPrice ?? null;
  const priceIsDraft =
    !readOnly &&
    (sellingPrice === "" ? null : parseFloat(sellingPrice)) !==
      (savedSellingPrice == null ? null : Number(savedSellingPrice));

  const foodCostPercent = costSummary?.foodCostPercent ?? null;
  const grossMarginPercent = costSummary?.grossMarginPercent ?? null;

  const recipeYield = costSummary?.recipeYield ?? null;
  const uncostedCount = costSummary?.uncostedLineCount ?? 0;
  // A total assembled from lines the rollup could not price is a floor. Saying
  // so is the whole point of T-604's per-line reasons — a number presented as
  // the answer when it is a minimum is how a plate gets underpriced.
  const isIncomplete = costSummary != null && !costSummary.isComplete;

  return (
    <div className="flex flex-col gap-6 px-6">
      {/* Section heading */}
      <div className="pl-2">
        <h2 className="text-2xl font-medium text-(--color-text-primary)">{t`Cost & Financials`}</h2>
      </div>

      <div className="flex flex-col gap-6 px-3">
        {/* Pricing header row */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium text-(--color-text-primary)">{t`Pricing`}</p>
            <p className="text-sm text-(--color-text-secondary)">
              {priceIsDraft
                ? t`Apply the price to update food cost and margin.`
                : t`Optional selling price to derive food cost and margin.`}
            </p>
          </div>
          {foodCostPercent !== null && grossMarginPercent !== null && (
            <div className="flex items-center gap-2">
              <span
                aria-label={t`Food cost`}
                className="inline-flex h-8 items-center justify-center rounded-full bg-(--color-warning) px-3 text-sm font-medium text-(--brand-white)"
              >
                {isIncomplete ? "≥ " : ""}
                {Math.round(foodCostPercent)}% {t`food cost`}
              </span>
              <span
                aria-label={t`Gross margin`}
                className="inline-flex h-8 items-center justify-center rounded-full bg-(--color-surface-accent) px-3 text-sm font-medium text-(--color-text-primary)"
              >
                {isIncomplete ? "≤ " : ""}
                {Math.round(grossMarginPercent)}% {t`margin`}
              </span>
            </div>
          )}
        </div>

        {/* Selling price */}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Selling price`}</p>
          {readOnly ? (
            <p className="h-11 flex items-center text-sm text-(--color-text-primary)">
              {recipe.targetSellingPrice != null
                ? formatCurrency(recipe.targetSellingPrice)
                : <span className="text-(--color-text-secondary)">—</span>}
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-(--color-text-primary)">
                  $
                </span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApply()}
                  className="h-11 rounded-lg border-(--color-border) pl-7 shadow-sm focus-visible:ring-1"
                />
              </div>
              <Button
                onClick={handleApply}
                disabled={updateRecipe.isPending}
                className="h-11 w-25 shrink-0 rounded-[10px] bg-(--color-action) text-sm font-medium text-(--color-action-fg) hover:bg-(--color-action)/90"
              >
                {t`Apply`}
              </Button>
            </div>
          )}
        </div>

        {/* Stat cards */}
        <div className="flex gap-2">
          <StatCard
            icon={<CircleDollarSign className="size-5 text-(--color-text-secondary)" />}
            label={t`Total Recipe Cost`}
            value={
              isLoading
                ? null
                : isIncomplete
                  ? `≥ ${formatCurrency(costSummary?.totalRecipeCost)}`
                  : formatCurrency(costSummary?.totalRecipeCost)
            }
            subtext={
              isIncomplete
                ? t`A minimum — ${uncostedCount} of ${costSummary?.lines.length ?? 0} ingredients have no cost`
                : t`Based on current ingredient pricing`
            }
            loading={isLoading}
          />
          <StatCard
            icon={<LayoutList className="size-5 text-(--color-text-secondary)" />}
            label={t`Cost per Yield Unit`}
            value={isLoading ? null : formatCurrency(costSummary?.costPerYieldUnit)}
            subtext={
              recipeYield
                ? t`Based on yield: ${recipeYield.quantity} ${recipeYield.uom}`
                : t`Set a yield to calculate`
            }
            loading={isLoading}
          />
          <StatCard
            icon={<CookingPot className="size-5 text-(--color-text-secondary)" />}
            label={t`Cost per Portion`}
            value={isLoading ? null : formatCurrency(costSummary?.costPerPortion)}
            subtext={
              costSummary?.portions
                ? t`Across ${costSummary.portions} portions`
                : t`Set servings to calculate`
            }
            loading={isLoading}
          />
        </div>

        {isIncomplete && (
          <div className="flex items-start gap-2 rounded-lg border border-(--color-warning) bg-(--color-warning-bg) px-3 py-2">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-(--color-warning-text)" />
            <p className="text-sm text-(--color-warning-text)">
              {t`Some ingredients couldn't be costed, so this recipe costs at least this much. The Ingredients tab says why for each one.`}
            </p>
          </div>
        )}

        {costSummary && <BiggestCost summary={costSummary} />}
        {costSummary && recipe.recipeType === "PREP" && <PrepReadback recipeId={recipe.id} summary={costSummary} />}
      </div>
    </div>
  );
}

/** The line that drives the cost, as a share of the total ("Atlantic salmon · 77%"). */
function BiggestCost({ summary }: { summary: CostSummary }) {
  const { t } = useLingui();
  const biggest = summary.lines.reduce<CostSummary["lines"][number] | null>(
    (top, line) => (line.extendedCost != null && (top?.extendedCost == null || line.extendedCost > top.extendedCost) ? line : top),
    null,
  );
  if (!biggest?.extendedCost || !(summary.totalRecipeCost > 0)) return null;
  const share = Math.round((biggest.extendedCost / summary.totalRecipeCost) * 100);
  return (
    <p className="text-sm text-(--color-text-secondary)">
      {t`Biggest cost`}:{" "}
      <span className="font-medium text-(--color-text-primary)">{t`${biggest.name} · ${share}%`}</span>
    </p>
  );
}

/** The yield's unit in the reference table, however the recipe spelled it ("L", "liter"). */
function findUom(uoms: Uom[], unit: string): Uom | null {
  const spelled = unit.trim();
  return (
    uoms.find(
      (u) => u.code === spelled.toLowerCase() || u.label === spelled.toLowerCase() || u.aliases.includes(spelled.toUpperCase()),
    ) ?? null
  );
}

const BASE_LABEL: Record<Uom["type"], string> = { WEIGHT: "g", LIQUID: "mL", EACH: "each" };

/**
 * What a batch of a prep recipe makes and what a share of it costs, the units
 * other recipes can use it in, and which recipes do.
 */
function PrepReadback({ recipeId, summary }: { recipeId: number; summary: CostSummary }) {
  const { t } = useLingui();
  const { data: uoms } = useUoms();
  const { data: usedIn, isLoading: usedInLoading, isError: usedInError, refetch: refetchUsedIn } = useRecipeUsedIn(recipeId);
  const batch = summary.recipeYield;
  const uom = batch && uoms ? findUom(uoms, batch.uom) : null;

  const readback = (() => {
    if (!batch || !uom || !uoms || !(batch.quantity > 0)) return null;
    const base = batch.quantity * uom.toBase;
    const baseLabel = BASE_LABEL[uom.type];
    const perBase = summary.totalRecipeCost / base;
    // Per base unit and per something a cook would buy by: the yield's own
    // unit, or the thousand-fold unit when the yield is already in the base.
    const larger =
      uom.toBase !== 1 ? uom : uoms.find((u) => u.type === uom.type && u.toBase === 1000) ?? null;
    const units = uoms.filter((u) => u.isRecipe && u.type === uom.type);
    const cup = units.find((u) => u.code === "cup");
    return {
      makes:
        uom.toBase !== 1
          ? t`One batch makes ${formatNumber(batch.quantity)} ${uomLabel(uom)} (${formatNumber(base, 0)} ${baseLabel}) and costs ${formatMoney(summary.totalRecipeCost)}.`
          : t`One batch makes ${formatNumber(batch.quantity)} ${uomLabel(uom)} and costs ${formatMoney(summary.totalRecipeCost)}.`,
      rates: [
        t`${formatMoney(perBase)} per ${baseLabel}`,
        ...(larger ? [t`${formatMoney(perBase * larger.toBase)} per ${uomLabel(larger)}`] : []),
      ].join(" · "),
      units: units.map(uomLabel).join(", ") + (cup ? ` (${t`1 cup = ${formatNumber(cup.toBase, 0)} mL`})` : ""),
    };
  })();

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-(--color-border) p-4">
      <div className="flex flex-col gap-1">
        <p className="text-xs font-bold tracking-wide text-(--color-text-secondary) uppercase">{t`Readback`}</p>
        {readback ? (
          <>
            <p className="text-base font-medium text-(--color-text-primary)">{readback.makes}</p>
            <p className="text-sm text-(--color-text-secondary)">{readback.rates}</p>
          </>
        ) : (
          <p className="text-sm text-(--color-text-secondary)">
            {t`Set a batch yield in g, mL or each to see what a share of this costs.`}
          </p>
        )}
      </div>
      {readback && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Recipes can use it by`}</p>
          <p className="text-sm text-(--color-text-secondary)">{readback.units}</p>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-(--color-text-primary)">
          {usedIn ? plural(usedIn.length, { one: "Used in # recipe", other: "Used in # recipes" }) : t`Used in recipes`}
        </p>
        <RecipeUsageListing
          lines={usedIn}
          isLoading={usedInLoading}
          isError={usedInError}
          onRetry={() => void refetchUsedIn()}
          emptyMessage={t`No recipe uses this prep recipe yet.`}
        />
        <p className="text-xs text-(--color-text-secondary)">{t`Prep recipes don't appear in Add menu item.`}</p>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: string | null;
  subtext: string;
  loading: boolean;
}

function StatCard({ icon, label, value, subtext, loading }: StatCardProps) {
  return (
    <div className={cn(statCardSurface, "flex h-38.5 flex-1 flex-col justify-between p-3")}>
      <div className="flex items-center gap-2">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-(--color-surface-accent)">
          {icon}
        </div>
        <p className="text-sm text-(--color-text-secondary)">{label}</p>
      </div>
      <div className="flex flex-col gap-2">
        {loading ? (
          <>
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3.5 w-32" />
          </>
        ) : (
          <>
            <p className="text-[32px] font-medium leading-[1.4] text-(--color-text-primary)">
              {value ?? "—"}
            </p>
            <p className="text-sm text-(--color-text-secondary)">{subtext}</p>
          </>
        )}
      </div>
    </div>
  );
}
